import { type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { startAudioRecording, type AudioRecordingSession } from '../lib/recorder';
import { recognizeCantonese } from './browserVoice';
import { assertDiscussionAudio, assertDiscussionCaptureCapacity, discussionCaptureGeneration, flushDiscussionCaptures, pendingDiscussionCaptures, retryDiscussionCaptures, saveDiscussionCapture, subscribeDiscussionCaptures, type DiscussionCapture, type DiscussionDraftMetadata } from './discussionCapture';

function errorMessage(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}

/** A short local recording test. No audio is uploaded by this component. */
export function MicrophoneCheck({ onReady, disabled = false }: { onReady?: (ready: boolean) => void; disabled?: boolean }) {
  const [phase, setPhase] = useState<'idle' | 'permission' | 'recording' | 'ready' | 'error'>('idle');
  const [level, setLevel] = useState(0);
  const [playbackUrl, setPlaybackUrl] = useState('');
  const [message, setMessage] = useState('按一下測試，錄音 3 秒。Click to make a 3-second microphone test.');
  const statusId = useId();
  const mounted = useRef(false);
  const attempt = useRef(0);
  const session = useRef<AudioRecordingSession | null>(null);
  const timeout = useRef<number | null>(null);
  const localUrl = useRef('');
  const audio = useRef<HTMLAudioElement | null>(null);
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      attempt.current += 1;
      if (timeout.current !== null) window.clearTimeout(timeout.current);
      session.current?.cancel();
      session.current = null;
      if (audio.current) { audio.current.pause(); audio.current.removeAttribute('src'); }
      if (localUrl.current) URL.revokeObjectURL(localUrl.current);
      localUrl.current = '';
    };
  }, []);

  useEffect(() => {
    if (!disabled) { setMessage('按一下測試，錄音 3 秒。Click to make a 3-second microphone test.'); return; }
    attempt.current += 1;
    if (timeout.current !== null) window.clearTimeout(timeout.current);
    timeout.current = null;
    session.current?.cancel(); session.current = null;
    if (audio.current) { audio.current.pause(); audio.current.removeAttribute('src'); }
    if (localUrl.current) URL.revokeObjectURL(localUrl.current);
    localUrl.current = '';
    setPlaybackUrl(''); setLevel(0); setPhase('idle');
    setMessage('請先儲存資料處理同意，再測試咪高峰。Save your data consent before checking the microphone.');
    onReadyRef.current?.(false);
  }, [disabled]);

  async function check() {
    if (disabledRef.current || phase === 'permission' || phase === 'recording') return;
    const currentAttempt = ++attempt.current;
    const active = () => mounted.current && !disabledRef.current && attempt.current === currentAttempt;
    setPhase('permission');
    setLevel(0);
    setMessage('請允許咪高峰存取。Please allow microphone access.');
    onReadyRef.current?.(false);
    if (audio.current) { audio.current.pause(); audio.current.removeAttribute('src'); }
    if (localUrl.current) URL.revokeObjectURL(localUrl.current);
    localUrl.current = '';
    setPlaybackUrl('');

    let recording: AudioRecordingSession | null = null;
    try {
      recording = await startAudioRecording({
        onLevel(value) {
          if (active()) setLevel(Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0);
        },
      });
      if (!active()) {
        recording.cancel();
        return;
      }
      session.current = recording;
      setPhase('recording');
      setMessage('請講一句廣東話，3 秒後自動停止。Say a short sentence; recording stops after 3 seconds.');
      const capturedSession = recording;
      timeout.current = window.setTimeout(() => {
        timeout.current = null;
        void finish(capturedSession);
      }, 3_000);
    } catch (error) {
      recording?.cancel();
      session.current = null;
      if (!active()) return;
      setPhase('error');
      setLevel(0);
      setMessage(errorMessage(error, '未能啟動咪高峰。The microphone could not be started.'));
      onReadyRef.current?.(false);
    }

    async function finish(capturedSession: AudioRecordingSession) {
      try {
        const blob = await capturedSession.stop();
        if (!active()) return;
        if (blob.size === 0) throw new Error('未有錄到聲音，請再試。No audio was captured. Please try again.');
        const url = URL.createObjectURL(blob);
        localUrl.current = url;
        setPlaybackUrl(url);
        setPhase('ready');
        setLevel(0);
        setMessage('錄音測試完成，請重播確認聲音。Recording captured. Play it back to check your sound.');
        onReadyRef.current?.(true);
      } catch (error) {
        if (!active()) return;
        setPhase('error');
        setLevel(0);
        setMessage(errorMessage(error, '錄音測試失敗，請再試。Microphone test failed. Please try again.'));
        onReadyRef.current?.(false);
      } finally {
        capturedSession.cancel();
        if (session.current === capturedSession) session.current = null;
      }
    }
  }

  const checking = phase === 'permission' || phase === 'recording';
  return (
    <div className="cnt-mic-check">
      <button type="button" className="secondary-button" onClick={() => void check()} disabled={disabled || checking} aria-describedby={statusId}>
        {checking ? '測試中 Testing…' : phase === 'ready' ? '再測一次 Test again' : '測試咪高峰 Test microphone'}
      </button>
      <meter className="cnt-meter" min={0} max={1} value={level} aria-label="即時咪高峰音量 Microphone level" />
      <p id={statusId} className="cnt-input-status" role={phase === 'error' ? 'alert' : 'status'}>{message}</p>
      {playbackUrl ? <audio ref={element => { if (element) audio.current = element; }} controls src={playbackUrl} aria-label="重播咪高峰測試 Play microphone test" /> : null}
      <small>測試錄音只留喺此頁，離開後會釋放。Test audio stays on this page and is released when you leave.</small>
    </div>
  );
}

/** Speech only fills the editable draft; every response requires an explicit send. */
export function SpeechComposer({ disabled = false, onSubmit, draftUpdate, onBusyChange, onRecording, sessionId = 'dictation' }: { disabled?: boolean; onSubmit: (text: string, options?: { requestId?: string }) => Promise<void>; draftUpdate?: { text: string; sequence: number; requestId?: string; transcript?: string }; onBusyChange?: (busy: boolean) => void; onRecording?: (capture: DiscussionCapture) => Promise<void>; sessionId?: string }) {
  const [draft, setDraft] = useState('');
  const [dictationPhase, setDictationPhase] = useState<'idle' | 'preparing' | 'listening' | 'saving'>('idle');
  const [, captureUpdate] = useState(0);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('輸入或用語音填入回應，再按發送。Type or dictate your response, then press Send.');
  const [hasError, setHasError] = useState(false);
  const inputId = useId();
  const statusId = useId();
  const mounted = useRef(false);
  const operation = useRef(0);
  const recognition = useRef<AbortController | null>(null);
  const completion = useRef<Promise<void> | null>(null);
  const draftMetadata = useRef<DiscussionDraftMetadata | null>(null);
  const emptyCapture = useRef(false);
  const recordingCallback = useRef(onRecording); recordingCallback.current = onRecording;
  const busyRef = useRef(false);
  const busyCallback = useRef(onBusyChange); busyCallback.current = onBusyChange;
  const disabledRef = useRef(disabled); disabledRef.current = disabled;
  const listening = dictationPhase !== 'idle';

  function reportBusy(value: boolean) {
    if (busyRef.current === value) return;
    busyRef.current = value; busyCallback.current?.(value);
  }
  async function stopAndSaveDictation() {
    if (!recognition.current) return;
    const previous = recognition.current; recognition.current = null;
    if (recordingCallback.current) {
      previous.abort();
      if (mounted.current) { setDictationPhase('saving'); setMessage('咪高峰已停止，正在保存錄音。Microphone stopped; saving recording…'); }
      try { await completion.current; await flushDiscussionCaptures(sessionId); }
      catch (problem) { if (mounted.current) { setDictationPhase('idle'); setHasError(true); setMessage(errorMessage(problem, '錄音未保存，請重試。Recording save failed.')); reportBusy(true); } return; }
      if (mounted.current) { setDictationPhase('idle'); setHasError(emptyCapture.current); setMessage(emptyCapture.current ? '未捕捉到音訊樣本，呢次冇錄音。No audio samples captured.' : '錄音已保存，已辨認文字已保留。Recording saved; recognised text is kept.'); reportBusy(false); }
      return;
    }
    operation.current += 1; previous.abort(); reportBusy(false);
    if (mounted.current) {
      setDictationPhase('idle'); setHasError(false);
      setMessage('語音填入已停止；已辨認嘅文字已保留。Dictation stopped; recognised text is kept.');
    }
  }
  function cancelDictation() { void stopAndSaveDictation(); }
  async function retryRecording() {
    if (disabledRef.current) return;
    setDictationPhase('saving'); reportBusy(true); setMessage('正在重試保存錄音。Retrying recording save…');
    try { await retryDiscussionCaptures(sessionId); if (mounted.current) { setDictationPhase('idle'); setHasError(false); setMessage('錄音已保存，請檢查文字後發送。Recording saved; check the draft before sending.'); reportBusy(false); } }
    catch (problem) { if (mounted.current) { setDictationPhase('idle'); setHasError(true); setMessage(errorMessage(problem, 'Recording save failed.')); reportBusy(true); } }
  }

  useEffect(() => { if (draftUpdate) { setDraft(draftUpdate.text); draftMetadata.current = draftUpdate.requestId ? { requestId: draftUpdate.requestId, transcript: draftUpdate.transcript ?? draftUpdate.text } : null; } }, [draftUpdate?.sequence]);
  useEffect(() => subscribeDiscussionCaptures(() => captureUpdate(value => value + 1)), []);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false; operation.current += 1;
      const previous = recognition.current; recognition.current = null; previous?.abort(); reportBusy(false);
    };
  }, []);
  // Parent busy during a typed submission must not cancel that submission.
  // Only an owned microphone operation is cancelled when this control is disabled.
  useEffect(() => { if (disabled) cancelDictation(); }, [disabled]);

  async function dictate() {
    if (disabled || busyRef.current || recognition.current) return;
    if (onRecording) { try { assertDiscussionCaptureCapacity(sessionId); } catch (problem) { setHasError(true); setMessage((problem as Error).message); return; } }
    const run = new AbortController(); recognition.current = run;
    const uploadRecording = onRecording, recordingScope = sessionId;
    const recordingGeneration = discussionCaptureGeneration();
    emptyCapture.current = false;
    let finish!: () => void; const finished = new Promise<void>(resolve => { finish = resolve; }); completion.current = finished;
    const currentOperation = ++operation.current;
    const active = () => mounted.current && operation.current === currentOperation && !run.signal.aborted && !disabledRef.current;
    reportBusy(true); setDictationPhase('preparing'); setHasError(false);
    setMessage('準備咪高峰，請稍候。Connecting microphone…');
    try {
      const result = await recognizeCantonese({
        signal: run.signal,
        ...(uploadRecording ? { recordingSource: 'dictation' as const, onRecording: async (capture: DiscussionCapture) => {
          if (capture.blob.size <= 44) { emptyCapture.current = true; draftMetadata.current = null; assertDiscussionAudio(capture); }
          if (mounted.current && operation.current === currentOperation) { draftMetadata.current = { requestId: capture.id, transcript: capture.transcript ?? '' }; if (capture.transcript?.trim()) setDraft(capture.transcript.trim()); setDictationPhase('saving'); setMessage('咪高峰已關閉，正在保存錄音。Microphone off; saving recording…'); }
          await saveDiscussionCapture(recordingScope, capture, uploadRecording, recordingGeneration);
        } } : {}),
        onListening: () => { if (active()) { setDictationPhase('listening'); setMessage('正在聆聽廣東話，請講一句。Listening for a Cantonese response…'); } },
        onInterim: text => { if (active() && text.trim()) setDraft(text.trim()); },
      });
      if (!active()) return;
      const text = result.trim();
      if (!text) throw new Error('未辨認到說話，請再試或輸入文字。No speech was recognised. Try again or type your response.');
      setDraft(text); setMessage('語音已填入，請檢查後按發送。Speech added. Check the text, then press Send.');
    } catch (error) {
      if (!mounted.current || operation.current !== currentOperation) return;
      if (run.signal.aborted && !pendingDiscussionCaptures(recordingScope).length) return;
      setHasError(true);
      setMessage(`${errorMessage(error, '未能辨認語音。Speech recognition is unavailable.')} 你亦可以輸入文字。You can type instead.`);
    } finally {
      if (recognition.current === run) recognition.current = null;
      if (mounted.current && operation.current === currentOperation) { setDictationPhase('idle'); reportBusy(Boolean(uploadRecording && pendingDiscussionCaptures(recordingScope).length)); }
      finish(); if (completion.current === finished) completion.current = null;
    }
  }

  async function send(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const text = draft.trim();
    if (disabled || busyRef.current || pendingDiscussionCaptures(sessionId).length || !text) return;
    const currentOperation = ++operation.current;
    const active = () => mounted.current && operation.current === currentOperation;
    reportBusy(true); setSending(true); setHasError(false); setMessage('正在發送回應。Sending your response…');
    try {
      const metadata = draftMetadata.current;
      if (metadata && metadata.transcript.trim() === text) await onSubmit(text, { requestId: metadata.requestId }); else await onSubmit(text);
      if (!active()) return;
      setDraft(''); draftMetadata.current = null; setMessage('回應已發送。Response sent.');
    } catch (error) {
      if (!active()) return;
      setHasError(true); setMessage(errorMessage(error, '回應未能發送，請再試。Your response could not be sent. Please try again.'));
    } finally {
      if (active()) { setSending(false); reportBusy(false); }
    }
  }

  const busy = disabled || listening || sending;
  const pendingRecordings = pendingDiscussionCaptures(sessionId);
  return <form className="cnt-composer" onSubmit={event => void send(event)}>
    <label htmlFor={inputId}>你嘅回應 Your response</label>
    <textarea id={inputId} value={draft} onChange={event => setDraft(event.target.value)} rows={3} disabled={busy} aria-describedby={statusId} placeholder="用廣東話表達你嘅想法。Share your idea in Cantonese." />
    <div>
      <button type="button" className="secondary-button" onClick={() => void dictate()} disabled={busy || pendingRecordings.length > 0}>{dictationPhase === 'saving' ? '保存錄音 Saving…' : dictationPhase === 'preparing' ? '準備咪高峰 Connecting…' : listening ? '聆聽中 Listening…' : '用語音填入 Dictate'}</button>
      {listening && dictationPhase !== 'saving' && <button type="button" className="secondary-button" onClick={cancelDictation}>停止語音填入 Stop dictation</button>}
      {pendingRecordings.length > 0 && <button type="button" className="secondary-button" disabled={disabled || dictationPhase === 'saving'} onClick={() => void retryRecording()}>重試保存錄音 Retry recording upload</button>}
      <button type="submit" className="primary-button" disabled={busy || pendingRecordings.length > 0 || !draft.trim()}>{sending ? '發送中 Sending…' : '發送回應 Send'}</button>
    </div>
    <p id={statusId} className="cnt-input-status" role={hasError ? 'alert' : 'status'}>{message}</p>
    {onRecording && <small>語音填入會保存原錄音同原始辨認文字；修改後嘅文字會另外送出。Dictation keeps the recording and original recognised text; edited text is submitted separately.</small>}
  </form>;
}
